import { notifyError } from 'config/setupBugsnag';
import {
  MarketingProjectServerResponse,
  MarketingProjectClientResponse,
} from 'types';
import RestUtility from '../../restUtility';
import { marketingMaterialProjectsEndpoint } from '../../restUtility/restEndpoints';

export class MarketingProjectService {
  private restUtility: RestUtility;

  constructor() {
    this.restUtility = new RestUtility();
  }
  async getMarketingProjects(filters: {
    status?: string;
    projectId?: string;
    includeThumbnail?: boolean;
    perPageSize?: number;
    currentPageNumber?: number;
    skus?: string;
  }) {
    let queryString = '';
    try {
      if (filters.status) {
        queryString = queryString + `status=${filters.status}`;
      }
      if (filters.projectId) {
        queryString = queryString + `dh_project_ids=${filters.projectId}`;
      }
      if (filters.skus) {
        queryString = queryString + `skus=${filters.skus}`;
      }
      if (filters.includeThumbnail) {
        queryString = queryString + '&include=project';
      }
      if (filters.currentPageNumber && filters.perPageSize) {
        queryString =
          queryString +
          `&per_page=${filters.perPageSize}&page=${filters.currentPageNumber}`;
      }
      const response =
        await this.restUtility.get<MarketingProjectServerResponse>(
          `${marketingMaterialProjectsEndpoint}?${queryString}`
        );

      return this.buildProjectsForClient(response, filters.currentPageNumber);
    } catch (error) {
      notifyError('ApiError:: getMarketingProjects', error);
      throw error;
    }
  }

  async getProjectDetailsByProjectId(projectId: string) {
    try {
      const response =
        await this.restUtility.get<MarketingProjectServerResponse>(
          `${marketingMaterialProjectsEndpoint}?dh_project_ids=${projectId}`
        );
      return this.buildProjectForClient(response);
    } catch (error) {
      notifyError('ApiError:: getProjectDetailsByProjectId', error);
      throw error;
    }
  }

  async createProject(
    projectId: string,
    projectName: string,
    sku: string,
    status: string
  ) {
    try {
      const response = await this.restUtility.post(
        `${marketingMaterialProjectsEndpoint}`,
        {
          dh_project_id: projectId,
          dh_project_name: projectName,
          sku: sku,
          status: status,
        }
      );
      return response;
    } catch (error) {
      notifyError('ApiError:: createProject', error);
      throw error;
    }
  }

  async deleteProject(projectId: string) {
    try {
      const response = await this.restUtility.delete(
        `${marketingMaterialProjectsEndpoint}/${projectId}`
      );
      return response;
    } catch (error) {
      notifyError('ApiError:: deleteProject', error);
      throw error;
    }
  }

  buildProjectsForClient(
    marketingProjectResponse: MarketingProjectServerResponse,
    currentPageNumber?: number
  ) {
    const marketingProjects: {
      reviews: MarketingProjectClientResponse[];
      count: number;
      totalCount: number;
      totalPages: number;
      currentPageNumber?: number;
    } = {
      reviews: [],
      count: 0,
      totalCount: 0,
      totalPages: 0,
      currentPageNumber: currentPageNumber,
    };
    marketingProjectResponse.data.reviews.forEach(review => {
      marketingProjects.reviews.push({
        sku: review.sku,
        projectId: review.dh_project_id,
        projectName: review.dh_project_name,
        creationDate: review.created_at,
        status: review.status,
        thumbnailUrl: marketingProjectResponse.data.projects
          ? marketingProjectResponse.data.projects[review.dh_project_id]
          : '',
      });
    });

    marketingProjects.count = marketingProjectResponse.data.meta.count;
    marketingProjects.totalCount =
      marketingProjectResponse.data.meta.total_count;
    marketingProjects.totalPages =
      marketingProjectResponse.data.meta.total_pages;

    return marketingProjects;
  }

  buildProjectForClient(
    marketingProjectResponse: MarketingProjectServerResponse
  ) {
    const projectReview = marketingProjectResponse.data.reviews[0];
    let clientReview: MarketingProjectClientResponse = {
      sku: '',
      projectId: '',
      projectName: '',
      creationDate: '',
      status: '',
      thumbnailUrl: '',
    };
    if (projectReview) {
      clientReview = {
        sku: projectReview.sku,
        projectId: projectReview.dh_project_id,
        projectName: projectReview.dh_project_name,
        creationDate: '',
        status: projectReview.status,
        thumbnailUrl: '',
      };
    }
    return clientReview;
  }
}
